import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { RequireAuth } from '@dss/auth'
import { PortalOrdersScreen } from '../features/orders/PortalOrdersScreen'

/** /orders — список заказов с фильтрами и CSV-экспортом (D14 §«Шаг 9», S34).
 *  Защищён <RequireAuth>; permission-gating внутри экрана (без любого из
 *  client.orders.view / distributor.orders.view / platform.orders.view-all
 *  показывается warning Banner).
 *
 *  Файл назван `orders.index.tsx`, а НЕ `orders.tsx` — иначе TanStack Router
 *  делает `orders` parent для `orders.$id`, требует у parent-route
 *  `<Outlet />`, без которого detail-страница невидима. См. fix
 *  fix/admin-orders-detail-route (баг S35 admin variant).
 *
 *  DO-5: URL search-params — multi-select-фильтры (TT/territories/managers/
 *  clients) и statusCodes сериализуются в `?tradepointIds=1&tradepointIds=2`
 *  для shareable-ссылок. validateSearch гарантирует runtime-форму. */
const ordersSearchSchema = z
  .object({
    statusCodes: z.array(z.string()).optional(),
    tradepointIds: z.array(z.coerce.number().int()).optional(),
    clientIds: z.array(z.coerce.number().int()).optional(),
    territoryIds: z.array(z.coerce.number().int()).optional(),
    managerIds: z.array(z.coerce.number().int()).optional(),
  })
  .partial()

type OrdersSearch = z.infer<typeof ordersSearchSchema>

export const Route = createFileRoute('/orders/')({
  validateSearch: (search): OrdersSearch => {
    // TanStack-router отдаёт сырой record-объект (string | string[] | undefined
    // в значениях). Зодом нормализуем: одиночные значения превращаем в массивы,
    // невалидные — выкидываем (через .catch на каждом поле).
    const normalized: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(search as Record<string, unknown>)) {
      // TanStack умеет хранить массив "как массив", а одиночное значение —
      // как строку. Унифицируем к массиву перед zod-парсингом.
      normalized[key] = Array.isArray(value) ? value : value != null ? [value] : value
    }
    return ordersSearchSchema.parse(normalized)
  },
  component: OrdersRoute,
})

function OrdersRoute() {
  return (
    <RequireAuth>
      <PortalOrdersScreen />
    </RequireAuth>
  )
}
